import React, { useState } from "react";
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";
import PageHeader from "../../components/PageHeader";
import BusinessTypePicker from "../../components/BusinessTypePicker";
import { useUser } from "../../contexts/UserContext";
import { BUSINESS_TYPE, BusinessTypeKey, applyBusinessType } from "../../platform/config";
import { saveBusinessType } from "../../services/businessTypeService";

/**
 * The kind of business this is. Switching it only changes words, colours and
 * the starting suggestion lists; bookings, clients and settings stay as they are.
 */
const BusinessType: React.FC = () => {
  const { user } = useUser();
  const isOwner = user?.role !== "EMPLOYEE";
  const [type, setType] = useState<BusinessTypeKey>(BUSINESS_TYPE);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveBusinessType(type);
      // Reloads into the new words and colours.
      if (!applyBusinessType(saved)) toast.success("Saved.", { position: "top-right" });
    } catch (err: any) {
      const message = err?.response?.data?.message;
      toast.error((Array.isArray(message) ? message[0] : message) || "Could not save.", { position: "top-right" });
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 760, mt: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Business type"
        subtitle="Sets the words, colours and starting suggestion lists the app uses for you."
      />
      {!isOwner && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Only the account owner can change the business type.
        </Alert>
      )}
      <BusinessTypePicker value={type} onChange={setType} disabled={!isOwner || saving} />
      <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mt: 2 }}>
        Nothing in your data changes: bookings, clients, files and settings stay as they are. Allergy, condition
        and file type lists you have not edited switch to the new type's suggestions.
      </Typography>
      {isOwner && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            disableElevation
            disabled={type === BUSINESS_TYPE || saving}
            onClick={save}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: "none" }}
          >
            Save and apply
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BusinessType;
